// @ts-check
import CircularProgress from '@material-ui/core/CircularProgress';
import { LINE_HEIGHT, SPACES } from 'common/constants/sizes';
import * as LighthouseService from 'common/services/lighthouse.service';
import { HyperLink } from 'components/base/HyperLink';
import Icon from 'components/base/Icon';
import Input from 'components/base/Input';
import { Flex, InputWrapper, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { useValidate } from 'components/hooks/useValidate';
import shortcutLogo from 'images/logos/shortcut-logo.svg';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  createCHSensorAuth,
  getAzureBoardsOrCHReauthSensorBody,
  validateCHToken
} from '../../../services/project-management.service';
import styled from 'styled-components';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import { updateSensorAuthorization } from 'state/callers';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { SENSOR_STATES } from '@linearb/linjs-common';
import { LBButton } from '@linearb/buzz';

const Logo = styled.img`
  height: 1.8rem;
`;

const ConnectView = ({ createSensorAuthorization, isTeamLicense }) => {
  const dispatch = useDispatch();
  const pmSensor = useSelector(pmIntegrationSelector);
  const isReAuth = (pmSensor && pmSensor?.state !== SENSOR_STATES.ok) || false;
  const [token, setToken] = useState('');
  const { isValid, error, isLoading, validationResponse } = useValidate({
    validateFn: validateCHToken,
    text: token,
    debounce: 1000
  });

  const actionWord = isReAuth ? 'Reauthorize' : 'Connect';

  const onReauth = async () => {
    const body = getAzureBoardsOrCHReauthSensorBody({ newToken: token });
    dispatch(updateSensorAuthorization(pmSensor.id, body));
  };

  const onCreate = () => {
    createCHSensorAuth({
      createSensorAuthorization,
      token,
      username: _.get(validationResponse, 'username', null),
      isTeamLicense
    });
  };

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.SHORTCUTS,
      click_type: TRACKING.CLICK.SETUP_PM_CONNECT_START
    });
  }, []);

  return (
    <>
      <Flex items='center' pb='medium'>
        <Logo src={shortcutLogo} alt='' />
        <Text
          size='large'
          weight='500'
          pl={SPACES.small}
          textTransform='uppercase'
        >
          {actionWord} Shortcut
        </Text>
      </Flex>
      <Text
        size='medium'
        color='shuttleGray'
        lineHeight={LINE_HEIGHT.medium}
        pb={SPACES.medium}
      >
        Connecting Shortcut will add your projects to your LinearB views.
        <br />
        Just get a Shortcut API token and enter it below:
      </Text>
      <InputWrapper>
        <Input
          style={{
            outline: 'none'
          }}
          dataCy='chTokenInput'
          height='3rem'
          type='text'
          name='chToken'
          width='36rem'
          placeholder={`Insert Shortcut Token`}
          onChange={setToken}
          isError={!isLoading && error}
          icon={
            isLoading ? (
              <CircularProgress size={20} />
            ) : error ? (
              <Icon name='ErrorIcon' color='torchRed' />
            ) : isValid ? (
              <Icon name='CheckIcon' color='pastelGreen' />
            ) : null
          }
        />
      </InputWrapper>
      <Flex items='center' pt='normal'>
        <LBButton
          disabled={!isValid}
          data-cy='shortcut-connect-with-token'
          data-testid='shortcut-connect-with-token'
          onClick={() => {
            isReAuth ? onReauth() : onCreate();
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              page: TRACKING.PAGES.SETTINGS,
              page_tab: TRACKING.PAGE_TABS.SHORTCUTS,
              click_type: TRACKING.CLICK.SETUP_PM_CONNECT_COMPLETED
            });
          }}
        >
          {actionWord}
        </LBButton>
        {error ? (
          <Text pl={SPACES['small']} size='medium' color='torchRed'>
            The token is not valid
          </Text>
        ) : null}
      </Flex>

      <Padding t='medium'>
        <HyperLink
          size='medium'
          onClick={() => {
            LighthouseService.navigateArticle('2nrk696o3k');
          }}
        >
          How do I get a Shortcut API token?
        </HyperLink>
      </Padding>
    </>
  );
};

export default ConnectView;
